import { create } from 'zustand';
import { Package, Status, PackageType, HistoryEntry } from '@/types';
import { packagesData } from '@/data/packages';

// Convert raw data to Package type
const initialPackages: Package[] = packagesData.map((data, index) => ({
  id: index + 1,
  sequential_number: (index % 9999) + 1,
  tracking: data.tracking,
  guest_name: data.guestName,
  room_number: "",
  entry_date: new Date().toISOString(),
  status: "Pending" as Status,
  last_updated_by: "System",
  history: [
    {
      status: "Pending" as Status,
      date: new Date().toISOString(),
      updatedBy: "System"
    }
  ],
  observations: data.observations || { oversized: false, overweight: false },
  is_deleted: false,
  deletion_requested_at: null
}));

interface PackageState {
  packages: Package[];
  filteredPackages: Package[];
  statusFilter: Status | 'All';
  searchQuery: string;
  startDate: string | null;
  endDate: string | null;
  addPackage: (pkg: Omit<Package, 'id' | 'last_updated_by' | 'history' | 'status'>) => void;
  updateStatus: (id: number, status: Status, updatedBy: string, signature?: string, receiverName?: string) => void;
  updatePackage: (id: number, data: Partial<Package>, updatedBy: string) => void;
  deletePackage: (id: number) => void;
  setPackages: (packages: Package[]) => void;
  filterByStatus: (status: Status | 'All') => void;
  searchPackages: (query: string) => void;
  setDateRange: (start: string | null, end: string | null) => void;
  clearFilters: () => void;
  toggleObservation: (id: number, observation: 'oversized' | 'overweight') => void;
}

// Helper function to filter packages based on all active filters
const applyAllFilters = (
  packages: Package[],
  searchQuery: string,
  statusFilter: Status | 'All',
  startDate: string | null,
  endDate: string | null
) => {
  return packages.filter(p => {
    // Apply search filter
    const matchesSearch = p.guest_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.tracking.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.room_number.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Apply status filter
    const matchesStatus = statusFilter === 'All' || p.status === statusFilter;
    
    // Apply date filter if dates are provided
    let matchesDateRange = true;
    if (startDate) {
      matchesDateRange = matchesDateRange && new Date(p.entry_date) >= new Date(startDate);
    }
    if (endDate) {
      matchesDateRange = matchesDateRange && new Date(p.entry_date) <= new Date(endDate);
    }
    
    return matchesSearch && matchesStatus && matchesDateRange;
  });
};

export const usePackageStore = create<PackageState>((set) => ({
  packages: initialPackages,
  filteredPackages: initialPackages,
  statusFilter: 'All',
  searchQuery: '',
  startDate: null,
  endDate: null,
  addPackage: (pkg) => set((state) => {
    const now = new Date().toISOString();
    const createdBy = "System";
    const newPackage: Package = {
      id: state.packages.length > 0 ? Math.max(...state.packages.map(p => p.id)) + 1 : 1,
      ...pkg,
      status: "Pending",
      last_updated_by: createdBy,
      history: [
        { status: "Created" as Status, date: now, updatedBy: createdBy },
        { status: "Pending" as Status, date: now, updatedBy: createdBy }
      ],
      observations: pkg.observations || { oversized: false, overweight: false },
      is_deleted: false,
      deletion_requested_at: null
    };
    const updatedPackages = [...state.packages, newPackage];
    // ✅ Log package creation
    console.log('[📦 Package] Created:', {
      id: newPackage.id,
      guestName: newPackage.guest_name,
      trackingNumber: newPackage.tracking,
      createdBy: newPackage.last_updated_by || 'unknown',
      timestamp: now,
    });
    return { 
      packages: updatedPackages,
      filteredPackages: applyAllFilters(
        updatedPackages, 
        state.searchQuery, 
        state.statusFilter,
        state.startDate,
        state.endDate
      )
    };
  }),
  updateStatus: (id, status, updatedBy, signature, receiverName) => set((state) => {
    const now = new Date().toISOString();
    const updatedPackages = state.packages.map(pkg =>
      pkg.id === id ? {
        ...pkg,
        status: status,
        last_updated_by: updatedBy,
        signature: status === "Delivered" && signature ? signature : pkg.signature,
        receiver_name: status === "Delivered" && receiverName ? receiverName : pkg.receiver_name,
        history: [
          ...((pkg.history || []).map(h => ({ ...h, status: h.status as Status }) as HistoryEntry)),
          { status: status as Status, date: now, updatedBy, ...(signature ? { signature } : {}), ...(receiverName ? { receiverName } : {}) }
        ]
      } : pkg
    );
    // 📥 Log package archive (if status is Archived)
    if (status === 'Archived') {
      const archivedPackage = state.packages.find(pkg => pkg.id === id);
      if (archivedPackage) {
        console.log('[📦 Package] Archived:', {
          id: archivedPackage.id,
          guestName: archivedPackage.guest_name,
          trackingNumber: archivedPackage.tracking,
          archivedBy: updatedBy || 'unknown',
          timestamp: now,
        });
      }
    }
    return {
      packages: updatedPackages,
      filteredPackages: applyAllFilters(
        updatedPackages, 
        state.searchQuery, 
        state.statusFilter,
        state.startDate,
        state.endDate
      )
    };
  }),
  updatePackage: (id, data, updatedBy) => set((state) => {
    const now = new Date().toISOString();
    const updatedPackages = state.packages.map(pkg => {
      if (pkg.id !== id) return pkg;
      // Determine if this is a true edit (not just a status change)
      const isEdit = (
        (data.guest_name && data.guest_name !== pkg.guest_name) ||
        (data.tracking && data.tracking !== pkg.tracking) ||
        (data.room_number && data.room_number !== pkg.room_number)
      );
      return {
        ...pkg,
        ...data,
        last_updated_by: updatedBy,
        history: isEdit
          ? [
              ...((pkg.history || []).map(h => ({ ...h, status: h.status as Status }) as HistoryEntry)),
              { status: "Edited" as Status, date: now, updatedBy }
            ]
          : (pkg.history || []).map(h => ({ ...h, status: h.status as Status }) as HistoryEntry)
      };
    });
    // ✏️ Log package update
    const updatedPackage = updatedPackages.find(pkg => pkg.id === id);
    if (updatedPackage) {
      console.log('[📦 Package] Updated:', {
        id: updatedPackage.id,
        updatedFields: {
          guestName: updatedPackage.guest_name,
          trackingNumber: updatedPackage.tracking,
          // Add any other edited fields as needed
        },
        updatedBy: updatedBy || 'unknown',
        timestamp: now,
      });
    }
    return {
      packages: updatedPackages,
      filteredPackages: applyAllFilters(
        updatedPackages, 
        state.searchQuery, 
        state.statusFilter,
        state.startDate,
        state.endDate
      )
    };
  }),
  deletePackage: (id) => set((state) => {
    const deletedPackage = state.packages.find(pkg => pkg.id === id);
    const updatedPackages = state.packages.map(pkg =>
      pkg.id === id ? { ...pkg, status: "Deleted" as Status } : pkg
    );
    // 🗑️ Log package deletion
    if (deletedPackage) {
      console.log('[📦 Package] Deleted:', {
        id: deletedPackage.id,
        guestName: deletedPackage.guest_name,
        trackingNumber: deletedPackage.tracking,
        deletedBy: deletedPackage.last_updated_by || 'unknown',
        timestamp: new Date().toISOString(),
      });
    }
    return {
      packages: updatedPackages,
      filteredPackages: applyAllFilters(
        updatedPackages, 
        state.searchQuery, 
        state.statusFilter,
        state.startDate,
        state.endDate
      )
    };
  }),
  setPackages: (packages) => set((state) => {
    // Ensure all packages have the required fields and correct types
    const normalizedPackages = packages.map(pkg => {
      let history: HistoryEntry[] = [];
      if (typeof pkg.history === "string") {
        try {
          const parsed = JSON.parse(pkg.history);
          if (Array.isArray(parsed)) {
            history = parsed.map(h => ({
              ...h,
              status: h.status as Status
            })) as HistoryEntry[];
          }
        } catch {
          history = [];
        }
      } else if (Array.isArray(pkg.history)) {
        history = pkg.history.map(h => ({
          ...h,
          status: h.status as Status
        })) as HistoryEntry[];
      }
      return {
        ...pkg,
        status: pkg.status as Status,
        is_deleted: pkg.is_deleted ?? false,
        deletion_requested_at: pkg.deletion_requested_at ?? null,
        history
      } as Package;
    });
    const filtered = applyAllFilters(
      normalizedPackages,
      state.searchQuery,
      state.statusFilter,
      state.startDate,
      state.endDate
    );
    console.log('[zustand] setPackages called. Filtered:', filtered.length, 'Total:', normalizedPackages.length);
    return {
      packages: normalizedPackages,
      filteredPackages: filtered
    };
  }),
  filterByStatus: (status) => set((state) => {
    const filtered = applyAllFilters(
      state.packages,
      state.searchQuery,
      status,
      state.startDate,
      state.endDate
    );
    console.log('[zustand] filterByStatus called. Status:', status, 'Filtered:', filtered.length);
    return {
      statusFilter: status,
      filteredPackages: filtered
    };
  }),
  searchPackages: (query) => set((state) => {
    const filtered = applyAllFilters(
      state.packages,
      query,
      state.statusFilter,
      state.startDate,
      state.endDate
    );
    console.log('[zustand] searchPackages called. Query:', query, 'Filtered:', filtered.length);
    return {
      searchQuery: query,
      filteredPackages: filtered
    };
  }),
  setDateRange: (start, end) => set((state) => {
    return {
      startDate: start,
      endDate: end,
      filteredPackages: applyAllFilters(
        state.packages,
        state.searchQuery,
        state.statusFilter,
        start,
        end
      )
    };
  }),
  clearFilters: () => set((state) => {
    return {
      statusFilter: 'All',
      searchQuery: '',
      startDate: null,
      endDate: null,
      filteredPackages: state.packages
    };
  }),
  toggleObservation: (id, observation) => set((state) => {
    const updatedPackages = state.packages.map(pkg =>
      pkg.id === id
        ? {
            ...pkg,
            observations: {
              ...pkg.observations,
              [observation]: !pkg.observations[observation]
            }
          }
        : pkg
    );
    return {
      packages: updatedPackages,
      filteredPackages: applyAllFilters(
        updatedPackages,
        state.searchQuery,
        state.statusFilter,
        state.startDate,
        state.endDate
      )
    };
  })
}));
